import { computed, toValue } from 'vue';
import { omit } from 'lodash';

import yardTasksService from '@satellite/services/yard-tasks-service';
import {
  transformFormDataToApiRequestData,
  transformTaskApiResponseToViewData
} from '../helpers/yardTaskUtils';

import type { Ref } from 'vue';
import type {
  CheckTaskFormData,
  MoveTaskFormData,
  TaskApiResponseData,
  TaskViewData
} from '@nova/core';

export interface UseYardTaskMutationsOptions {
  warehouseId?: string | Ref<string | null> | null;
  warehouseTimezone?: string | Ref<string | null> | null;
}

/**
 * Composable for creating and updating yard tasks
 * @param options - Options containing warehouseId and warehouseTimezone (can be reactive)
 * @returns Object with createTask and updateTask methods
 */
export function useYardTaskMutations(options?: UseYardTaskMutationsOptions) {
  const { warehouseId: providedWarehouseId, warehouseTimezone: providedWarehouseTimezone } =
    options || {};

  const warehouseId = computed(() => toValue(providedWarehouseId) || '');
  const warehouseTimezone = computed(() => toValue(providedWarehouseTimezone) || 'utc');

  /**
   * Creates a new yard task
   * @param formData - Form data for the task (check or move)
   * @returns Transformed task view data
   * @throws Error if payload is invalid or API call fails
   */
  const createTask = async (
    formData: CheckTaskFormData | MoveTaskFormData
  ): Promise<TaskViewData> => {
    const currentWarehouseId = warehouseId.value;
    const currentTimezone = warehouseTimezone.value;

    if (!currentWarehouseId) {
      throw new Error('Warehouse ID is required');
    }

    const payload = transformFormDataToApiRequestData(formData, {
      warehouseId: currentWarehouseId,
      warehouseTimezone: currentTimezone
    });

    if (!payload) {
      throw new Error('Failed to build task payload');
    }

    const response = (await yardTasksService.createYardTask(payload)) as TaskApiResponseData;

    if (!response) {
      throw new Error('Failed to create task: No response from server');
    }

    const viewData = transformTaskApiResponseToViewData(response);
    if (!viewData) {
      throw new Error('Failed to transform task response to view data');
    }

    return viewData;
  };

  /**
   * Updates an existing yard task
   * @param taskId - ID of the task to update
   * @param formData - Form data for the task (check or move, can be partial for patching)
   * @returns Transformed task view data
   * @throws Error if payload is invalid or API call fails
   */
  const updateTask = async (
    taskId: string,
    formData: Partial<CheckTaskFormData> | Partial<MoveTaskFormData>
  ): Promise<TaskViewData> => {
    const currentWarehouseId = warehouseId.value;
    const currentTimezone = warehouseTimezone.value;

    if (!taskId) {
      throw new Error('Task ID is required');
    }

    const payload = transformFormDataToApiRequestData(formData, {
      warehouseId: currentWarehouseId,
      warehouseTimezone: currentTimezone
    });

    if (!payload) {
      throw new Error('Failed to build task payload');
    }

    const response = (await yardTasksService.updateYardTask(
      taskId,
      omit(payload, 'type', 'warehouseId')
    )) as TaskApiResponseData;

    if (!response) {
      throw new Error('Failed to update task: No response from server');
    }

    const viewData = transformTaskApiResponseToViewData(response);
    if (!viewData) {
      throw new Error('Failed to transform task response to view data');
    }

    return viewData;
  };

  return {
    createTask,
    updateTask,
    warehouseId,
    warehouseTimezone
  };
}
